import { createTheme, alpha } from '@mui/material/styles';

/** Palette haut de gamme : ivoire, noir doux, sable, or discret, bordeaux profond */
const bordeaux = '#5C2434';
const bordeauxDark = '#421a28';
const ivory = '#FAF8F5';
const sand = '#C9B89A';
const sandLight = '#E8E0D4';
const softBlack = '#1C1B1A';
const gold = '#A8946C';

const radiusSm = 10;
const radiusMd = 14;

export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: bordeaux,
      light: '#7a3348',
      dark: bordeauxDark,
      contrastText: ivory,
    },
    secondary: {
      main: sand,
      light: sandLight,
      dark: '#9A8B72',
      contrastText: softBlack,
    },
    background: {
      default: ivory,
      paper: '#FFFFFF',
    },
    text: {
      primary: softBlack,
      secondary: '#4A4744',
    },
    divider: alpha(softBlack, 0.08),
    success: {
      main: '#2F4A3C',
    },
    info: {
      main: sand,
    },
  },
  shape: {
    borderRadius: radiusSm,
  },
  typography: {
    fontFamily: '"Outfit", "Inter", system-ui, sans-serif',
    h1: {
      fontFamily: '"Cormorant Garamond", "Playfair Display", Georgia, serif',
      fontWeight: 500,
      letterSpacing: '-0.02em',
      color: softBlack,
    },
    h2: {
      fontFamily: '"Cormorant Garamond", "Playfair Display", Georgia, serif',
      fontWeight: 500,
      letterSpacing: '-0.02em',
      color: softBlack,
    },
    h3: {
      fontFamily: '"Cormorant Garamond", "Playfair Display", Georgia, serif',
      fontWeight: 500,
      color: softBlack,
    },
    h4: {
      fontFamily: '"Cormorant Garamond", "Playfair Display", Georgia, serif',
      fontWeight: 500,
      color: softBlack,
    },
    h5: {
      fontFamily: '"Cormorant Garamond", "Playfair Display", Georgia, serif',
      fontWeight: 500,
      color: softBlack,
    },
    h6: {
      fontFamily: '"Cormorant Garamond", "Playfair Display", Georgia, serif',
      fontWeight: 600,
      color: softBlack,
    },
    subtitle1: {
      fontFamily: '"Outfit", sans-serif',
      fontWeight: 500,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      fontSize: '0.75rem',
      color: alpha(softBlack, 0.65),
    },
    body1: {
      fontFamily: '"Outfit", "Inter", sans-serif',
      fontSize: '1rem',
      lineHeight: 1.75,
    },
    body2: {
      fontFamily: '"Outfit", "Inter", sans-serif',
      lineHeight: 1.7,
    },
    button: {
      fontFamily: '"Outfit", sans-serif',
      fontWeight: 600,
      letterSpacing: '0.1em',
      textTransform: 'none',
      fontSize: '0.8125rem',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: ivory,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: radiusSm,
          padding: '11px 28px',
          boxShadow: 'none',
          transition: 'transform 0.2s ease, box-shadow 0.25s ease, background-color 0.2s ease, border-color 0.2s ease',
          '&:active': {
            transform: 'scale(0.98)',
          },
        },
        contained: {
          background: `linear-gradient(145deg, ${bordeaux} 0%, ${bordeauxDark} 100%)`,
          color: ivory,
          border: `1px solid ${alpha(gold, 0.4)}`,
          '&:hover': {
            boxShadow: `0 10px 28px ${alpha(bordeaux, 0.28)}`,
            background: bordeauxDark,
            transform: 'translateY(-1px)',
          },
        },
        outlined: {
          borderWidth: 1,
          borderRadius: radiusSm,
          borderColor: alpha(softBlack, 0.2),
          color: softBlack,
          backgroundColor: '#fff',
          '&:hover': {
            borderColor: bordeaux,
            color: bordeaux,
            backgroundColor: alpha(bordeaux, 0.04),
            transform: 'translateY(-1px)',
          },
        },
        text: {
          color: softBlack,
          letterSpacing: '0.06em',
          borderRadius: radiusSm,
          '&:hover': {
            backgroundColor: alpha(bordeaux, 0.06),
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          borderRadius: radiusMd,
          boxShadow: '0 1px 0 rgba(28, 27, 26, 0.04), 0 16px 48px rgba(28, 27, 26, 0.04)',
        },
        elevation1: {
          boxShadow: '0 1px 0 rgba(28, 27, 26, 0.06), 0 12px 40px rgba(28, 27, 26, 0.05)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(ivory, 0.94),
          backdropFilter: 'blur(16px)',
          color: softBlack,
          boxShadow: '0 1px 0 rgba(28, 27, 26, 0.05)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: radiusMd,
          overflow: 'hidden',
          border: `1px solid ${alpha(softBlack, 0.06)}`,
          boxShadow: 'none',
          transition: 'transform 0.45s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.45s ease',
          '&:hover': {
            transform: 'translateY(-3px)',
            boxShadow: `0 20px 56px ${alpha(softBlack, 0.08)}`,
          },
        },
      },
    },
  },
});
